import { useFieldArray, useFormContext } from 'react-hook-form';
import { BoolRadioField } from '@views/register/history/history-fields';
import type { TLocalize } from '@hooks/use-localize';
import type { IHistorySocialFormValues, IHistorySocialModel } from '@/types/history/history-social';
import FieldError from '@components/field-error';

interface IProps {
    model: IHistorySocialModel;
    t: TLocalize;
}

const DrugsSection = ({ model, t }: IProps) => {
    const {
        control,
        register,
        watch,
        formState: { errors },
    } = useFormContext<IHistorySocialFormValues>();
    const { fields, append, remove } = useFieldArray({ control, name: 'drugUseHistory' });

    const yes = t('Word_Yes', 'Yes');
    const no = t('Word_No', 'No');
    const pleaseSelect = t('DashLabel_PleaseSelect', 'Please Select');
    const deleteDrug = t('DashLabel_DeleteDrug', 'Delete drug');

    const rows = watch('drugUseHistory');

    // The treatment column only appears once at least one drug is marked as abused.
    const showTreatmentColumn = rows?.some((x) => x.hasAbusedDrug === '1') === true;

    return (
        <>
            <BoolRadioField
                name="hasUsedDrugs"
                label={t('DashLabel_HaveYouEverUsedRecreationalDrugsNotPrescribedByADo', 'Have you ever used recreational drugs not prescribed by a doctor')}
                yesLabel={yes}
                noLabel={no}
            />

            {watch('hasUsedDrugs') === '1' && (
                <div className="sub-question">
                    <div className="form-group">
                        <div className="col-xs-12">
                            <div className="help-block">
                                {t('DashLabel_PleaseListTheRecreationalDrugsYouHaveUsed', 'Please list the recreational drugs you have used')}:
                            </div>

                            <div className="row hidden-xs">
                                <div className="col-xs-4 col-sm-4">
                                    {t('DashLabel_DrugName', 'Drug Name')}
                                </div>
                                <div className="col-xs-4 col-sm-3">
                                    {t('DashLabel_HowLongAgo', 'How long ago')}?
                                </div>
                                <div className="col-xs-2 col-sm-2">
                                    {t('DashLabel_DidYouAbuseDrug', 'Did you abuse Drug')}?
                                </div>
                                {showTreatmentColumn && (
                                    <div className="col-xs-2 col-sm-2">
                                        {t('DashLabel_ReceivedTreatment', 'Received Treatment')}?
                                    </div>
                                )}
                            </div>

                            {fields.map((field, index) => {
                                const rowErrors = errors.drugUseHistory?.[index];
                                const hasAbused = rows?.[index]?.hasAbusedDrug;

                                return (
                                    <div className="row row-margined" key={field.id}>
                                        <div className="col-xs-12 col-sm-4">
                                            <select
                                                className="form-control"
                                                {...register(`drugUseHistory.${index}.recreationalDrug`)}
                                            >
                                                <option value="">{pleaseSelect}</option>
                                                {model.recreationalDrugOptions.map((o) => (
                                                    <option key={o.value} value={o.value}>
                                                        {o.label}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>

                                        <div className="col-xs-12 col-sm-3">
                                            <select
                                                className="form-control"
                                                {...register(`drugUseHistory.${index}.yearsAgo`)}
                                            >
                                                <option value="">{pleaseSelect}</option>
                                                {model.yearsAgoOptions.map((o) => (
                                                    <option key={o.value} value={o.value}>
                                                        {o.label}
                                                    </option>
                                                ))}
                                            </select>
                                            <FieldError message={rowErrors?.yearsAgo?.message} />
                                        </div>

                                        <div className="col-xs-12 col-sm-2">
                                            <label className="radio-inline">
                                                <input
                                                    type="radio"
                                                    value="1"
                                                    {...register(`drugUseHistory.${index}.hasAbusedDrug`)}
                                                />{' '}
                                                {yes}
                                            </label>
                                            <label className="radio-inline">
                                                <input
                                                    type="radio"
                                                    value="0"
                                                    {...register(`drugUseHistory.${index}.hasAbusedDrug`)}
                                                />{' '}
                                                {no}
                                            </label>
                                            <FieldError message={rowErrors?.hasAbusedDrug?.message} />
                                        </div>

                                        {showTreatmentColumn && (
                                            <div className="col-xs-12 col-sm-2">
                                                {hasAbused === '1' && (
                                                    <>
                                                        <label className="radio-inline">
                                                            <input
                                                                type="radio"
                                                                value="1"
                                                                {...register(`drugUseHistory.${index}.hasReceivedTreatment`)}
                                                            />{' '}
                                                            {yes}
                                                        </label>
                                                        <label className="radio-inline">
                                                            <input
                                                                type="radio"
                                                                value="0"
                                                                {...register(`drugUseHistory.${index}.hasReceivedTreatment`)}
                                                            />{' '}
                                                            {no}
                                                        </label>
                                                        <FieldError
                                                            message={rowErrors?.hasReceivedTreatment?.message}
                                                        />
                                                    </>
                                                )}
                                            </div>
                                        )}

                                        <div className="col-xs-12 col-sm-1">
                                            {index > 0 && (
                                                <button
                                                    type="button"
                                                    className="btn btn-danger"
                                                    aria-label={deleteDrug}
                                                    title={deleteDrug}
                                                    onClick={() => remove(index)}
                                                >
                                                    <span className="glyphicon glyphicon-trash" aria-hidden="true"></span>
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}

                            <div className="row">
                                <div className="col-xs-12">
                                    <button
                                        type="button"
                                        className="btn btn-add-entry btn-sm"
                                        style={{ width: 200 }}
                                        onClick={() =>
                                            append({
                                                recreationalDrug: '',
                                                yearsAgo: '',
                                                hasAbusedDrug: '',
                                                hasReceivedTreatment: '',
                                            })
                                        }
                                    >
                                        <span className="glyphicon glyphicon-plus-sign"></span>{' '}
                                        {t('DashLabel_AddDrug', 'Add Drug')}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default DrugsSection;
